import React, {useState, useEffect} from 'react';
import {useRouter} from 'next/router';
import _ from 'lodash';
import OverviewStep from './steps/OverviewStep';
import ProcessStep from './steps/ProcessStep';
import QuestionsStep from './steps/QuestionsStep';
import ReflectionsStep from './steps/ReflectionsStep';
import SubmittingStep from './steps/SubmittingStep';
import InterviewPreview from './InterviewPreview';
import MotivationModal from './MotivationModal';
import PersonaModal from './PersonaModal';
import SignupModal from '../../components/SignupModal';
import DocumentHeader from '../../components/DocumentHeader';
import {MinimalHeader} from '../../components/Header';
import {useAnalytics, useAppContext} from '../../hooks';
import Link from '../../components/Link';
import Button, {ButtonTypes} from '../../components/ButtonNew';
import {
  getTags, addInterview, patchInterview, getDraftInterview,
} from '../../api/api';
import {cx} from '../../util';
import {Feature} from '../../../../lib/constants/tags';
import {isEligibleForContributePersonaCTA} from '../../../../lib/constants/persona';
import DismissibleCallout from '../../components/DismissibleCallout';
import {getBrandName} from '../../components/Brand';

const LOCAL_STORAGE_KEY = 'contribution_form_draft';

const getLocalStorageDraft = () => {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

const STEPS = ['Share your interview', 'Process', 'Questions', 'Reflections', 'Submitting'];
const STEPS_SUBHEADERS = [
  (
    <span>
      {`Help the ${getBrandName()} community by sharing your recent interview experience anonymously.
        Interview experiences that are detailed and clearly written will be published to the community. `}
      <Link
        href="/guidelines"
        target="_blank"
        className="text-indigo-600 hover:text-indigo-700"
      >
        Review guidelines.
      </Link>
    </span>
  ),
  null,
  (
    <span>
      {`Share the questions you were asked during each stage – the more specific, the more helpful.
        Enter each question word-for-word as it was asked to help build our `}
      <Link
        href="/questions"
        target="_blank"
        className="text-indigo-600 hover:text-indigo-700"
      >
        question database
      </Link>
      .
    </span>
  ),
  null,
  null,
];

const StepIndicator = ({step}) => (
  <div className="flex flex-col gap-3 mb-8">
    {step < STEPS.length - 1 ? (
      <p className="text-sm tracking-tight">
        <span className="text-indigo-600 font-medium inline-block w-3 text-center">{step + 1}</span>
        <span className="text-gray-300">
          {' / '}
          {STEPS.length - 1}
        </span>
      </p>
    ) : (
      <p className="text-sm tracking-tight text-indigo-600 font-medium">Done!</p>
    )}
    <h2 className="text-2xl font-medium text-gray-800 tracking-tight">
      {STEPS[step]}
    </h2>
    {STEPS_SUBHEADERS[step] && (
      <p className="text-gray-500">
        {STEPS_SUBHEADERS[step]}
      </p>
    )}
  </div>
);

/**
 * Determine which step to resume on based on the fetched draft interview.
 */
const getResumeStep = (interview) => {
  if (!interview) return 0;
  if (!interview.company && !interview.role) return 0;
  if (!interview.data?.stages?.length) return 1;
  if (!interview.data?.stages?.some((s) => s.questions?.some((q) => q.text?.trim()))) return 2;
  return 3;
};

/**
 * Reconstruct the draft state from a fetched interview row.
 */
const slugToDisplayName = (slug, tagType, tagResults) => {
  const tag = _.find(tagResults?.[tagType] ?? [], ['id', slug]);
  return tag ? tag.name : slug;
};

const draftFromInterview = (interview, tagResults) => {
  if (!interview) return {};
  const d = interview.data || {};
  const stages = (d.stages || []).map((s) => ({
    ...s,
    interviewTypes: (s.interviewTypes || []).map(
      (id) => slugToDisplayName(id, 'type', tagResults),
    ),
  }));

  // Convert asked_on timestamp back to month/year form values
  let askedOnMonth = '';
  let askedOnYear = '';
  if (interview.asked_on) {
    const askedOnDate = new Date(interview.asked_on);
    askedOnMonth = askedOnDate.toLocaleString('en-US', {month: 'long'});
    askedOnYear = String(askedOnDate.getFullYear());
  }

  return {
    company: slugToDisplayName(interview.company || '', 'company', tagResults),
    role: interview.role || '',
    level: d.level || '',
    jobTitle: d.jobTitle || '',
    workType: d.workType || '',
    location: interview.location || '',
    askedOnMonth,
    askedOnYear,
    durationValue: d.durationValue || '',
    durationUnit: d.durationUnit || 'weeks',
    stages,
    result: d.result || '',
    overview: d.overview || '',
    difficulty: d.difficulty || 3,
    salary: d.salary || '',
    stock: d.stock || '',
    bonus: d.bonus || '',
    compComments: d.compComments || '',
    tips: d.tips || '',
    companyCulture: d.companyCulture || '',
    canFollowUp: d.canFollowUp ?? true,
  };
};

const INITIAL_DRAFT = {
  company: '',
  role: '',
  level: '',
  jobTitle: '',
  workType: '',
  location: '',
  askedOnMonth: '',
  askedOnYear: '',
  durationValue: '',
  durationUnit: 'weeks',
  stages: [],
  result: '',
  overview: '',
  difficulty: 3,
  salary: '',
  stock: '',
  bonus: '',
  compComments: '',
  tips: '',
  companyCulture: '',
  canFollowUp: true,
};

const AddInterviewPage = () => {
  const router = useRouter();
  const {tracker} = useAnalytics();
  const {currentUser, hasIxpAccess} = useAppContext();
  const {company: initialCompany, role: initialRole, skipDrafts} = router.query;

  const [step, setStep] = useState(0);
  const [incomingStep, setIncomingStep] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('idle'); // 'idle' | 'leaving' | 'entering'
  const [draft, setDraft] = useState({...INITIAL_DRAFT});
  const [interviewId, setInterviewId] = useState(null);
  const [loadedFromRemoteDraft, setLoadedFromRemoteDraft] = useState(false);
  const [tags, setTags] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showMotivationModal, setShowMotivationModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPersonaSkipModal, setShowPersonaSkipModal] = useState(false);
  const isResettingRef = React.useRef(false);

  // Warn before closing tab or navigating away via user action
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isResettingRef.current) return;
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const displayNameToSlug = (name, tagType = 'company') => {
    const tag = _.find(tags?.[tagType] ?? [], ['name', name]);
    return tag ? tag.id : name;
  };

  useEffect(() => {
    tracker.contributeView();

    const init = async () => {
      const tagResults = await getTags({feature: Feature.Questions});
      setTags(tagResults);

      // Build the draft from URL params, local storage, or remote draft

      // 1. URL params take highest priority
      if (skipDrafts || initialCompany || initialRole) {
        setDraft((prev) => ({
          ...prev,
          company: initialCompany ? slugToDisplayName(initialCompany, 'company', tagResults) : '',
          role: initialRole || '',
        }));
        return;
      }

      // 2. Check remote draft first because we may have put their interview back to draft
      // and we don't want localStorage to overwrite load. This is a no-op for logged out users
      const remoteDraft = await getDraftInterview().catch(() => null);
      if (remoteDraft) {
        setInterviewId(remoteDraft.id);
        const draftData = draftFromInterview(remoteDraft, tagResults);
        setDraft((prev) => ({...prev, ...draftData}));

        // Only with a remote draft can the user have made it past the first step
        setStep(getResumeStep(remoteDraft));
        setIncomingStep(getResumeStep(remoteDraft));
        setLoadedFromRemoteDraft(true);
        return;
      }

      // 3. Fall back to local storage (primary path for logged out users)
      const localDraft = getLocalStorageDraft();
      if (localDraft) {
        if (localDraft.company) {
          localDraft.company = slugToDisplayName(localDraft.company, 'company', tagResults);
        }
        setDraft((prev) => ({...prev, ...localDraft}));
        return;
      }

      // No draft found, starting fresh
      setShowMotivationModal(true);
    };

    init();
  }, []);

  const onBack = () => {
    setAnimationPhase('leaving');
    setIncomingStep((s) => Math.max(0, s - 1));
    setTimeout(() => {
      setAnimationPhase('entering');
      window.scrollTo({top: 0, behavior: 'instant'});
      setTimeout(() => {
        setStep((s) => Math.max(0, s - 1));
        setAnimationPhase('idle');
      }, 50);
    }, 200);
  };

  const onEdit = (values) => {
    setDraft((prev) => ({...prev, ...values}));

    // On only the first step, also store changes in local storage so that
    // we can restore for logged out users/before remote draft is created
    if (step === 0) {
      const localDraft = {...draft, ...values};
      if (localDraft.company) {
        localDraft.company = displayNameToSlug(localDraft.company);
      }
      localStorage.setItem(
        LOCAL_STORAGE_KEY,
        JSON.stringify(localDraft),
      );
    }
  };

  const advanceStep = () => {
    setAnimationPhase('leaving');
    setIncomingStep((s) => s + 1);
    setTimeout(() => {
      setAnimationPhase('entering');
      window.scrollTo({top: 0, behavior: 'instant'});
      setTimeout(() => {
        setStep((s) => s + 1);
        setAnimationPhase('idle');
      }, 50);
    }, 200);
  };

  const onContinuePastPersonaSkipCta = () => {
    tracker.contributeDismissPersonaSkipCta();
    setShowPersonaSkipModal(false);
    advanceStep();
  };

  const onNext = async (stepValues) => {
    if (step === 0 && !currentUser) {
      setShowSignupModal(true);
      return;
    }

    const overviewInterviewData = step === 0 ? {
      company: displayNameToSlug(stepValues.company),
      role: stepValues.role,
      location: stepValues.location,
      result: stepValues.result,
      level: stepValues.level || undefined,
      jobTitle: stepValues.jobTitle || undefined,
      workType: stepValues.workType || undefined,
    } : null;

    // Eligible Step 0 submits hold the transition and show the Persona skip-review
    // CTA instead of advancing immediately; every other case animates optimistically.
    const eligibleForPersonaCTA = step === 0
      && isEligibleForContributePersonaCTA(overviewInterviewData);

    const previousStep = step;
    if (!eligibleForPersonaCTA) {
      advanceStep();
    }

    const revertAnimation = () => {
      setIncomingStep(previousStep);
      setStep(previousStep);
      setAnimationPhase('idle');
    };

    try {
      if (step === 0) {
        // Step 0: Create or update the interview
        if (!interviewId) {
          // No interview yet, create new
          const interview = await addInterview(overviewInterviewData);
          setInterviewId(interview.id);
        } else {
          // Interview exists, patch it
          await patchInterview(interviewId, overviewInterviewData);
        }

        // Clear query params and local storage so reloading picks up the remote draft
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        router.replace('/questions/contribute', undefined, {shallow: true});

        tracker.contributeCompleteOverviewStep();

        if (eligibleForPersonaCTA) {
          tracker.contributeShowPersonaSkipCta();
          setShowPersonaSkipModal(true);
        }
      } else if (step === 1) {
        // Step 1: PATCH with interview process data
        const stages = (stepValues.stages || []).map((s) => ({
          ...s,
          interviewTypes: (s.interviewTypes || []).map(
            (typeName) => displayNameToSlug(typeName, 'type'),
          ),
        }));
        await patchInterview(interviewId, {
          askedOnMonth: stepValues.askedOnMonth || undefined,
          askedOnYear: stepValues.askedOnYear ? Number(stepValues.askedOnYear) : undefined,
          durationValue: stepValues.durationValue ? Number(stepValues.durationValue) : undefined,
          durationUnit: stepValues.durationUnit || undefined,
          stages,
        });
        tracker.contributeCompleteProcessStep();
      } else if (step === 2) {
        // Step 2: PATCH with questions added to stages array
        const stages = (stepValues.stages || []).map((s) => ({
          ...s,
          interviewTypes: (s.interviewTypes || []).map(
            (typeName) => displayNameToSlug(typeName, 'type'),
          ),
        }));
        await patchInterview(interviewId, {
          stages,
        });
        tracker.contributeCompleteQuestionsStep();
      } else if (step === 3) {
        // Step 3: Final submit
        setIsSubmitting(true);

        await patchInterview(interviewId, {
          status: 'pending',
          overview: stepValues.overview,
          difficulty: stepValues.difficulty ? Number(stepValues.difficulty) : undefined,
          salary: stepValues.salary ? Number(stepValues.salary) : undefined,
          stock: stepValues.stock ? Number(stepValues.stock) : undefined,
          bonus: stepValues.bonus ? Number(stepValues.bonus) : undefined,
          compComments: stepValues.compComments || undefined,
          tips: stepValues.tips || undefined,
          companyCulture: stepValues.companyCulture || undefined,
          canFollowUp: stepValues.canFollowUp,
        });

        tracker.contributeSuccessfulSubmit();
        setIsSubmitting(false);
        router.push('/experiences?src=contribute&submitted=true');
        return;
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      if (step === 3) setIsSubmitting(false);
      revertAnimation();
    }
  };

  const onReset = async () => {
    if (window.confirm('Are you sure you want to discard this interview and start a new one?')) {
      if (interviewId) {
        await patchInterview(interviewId, {status: 'archived'});
      }
      isResettingRef.current = true;
      router.reload();
    }
  };

  const stepProps = {
    draft, onNext, onBack, onEdit,
  };
  const displayStep = animationPhase === 'entering' ? incomingStep : step;

  return (
    <>
      <SignupModal
        isOpen={showSignupModal}
        source="contribute"
        onClose={() => setShowSignupModal(false)}
        returnPath="/questions/contribute"
      />
      <MotivationModal
        isOpen={showMotivationModal}
        onClose={() => setShowMotivationModal(false)}
        isLoggedIn={!!currentUser}
        hasIxpAccess={hasIxpAccess}
        userUuid={currentUser?.uuid}
      />
      <PersonaModal
        isOpen={showPersonaSkipModal}
        onClose={onContinuePastPersonaSkipCta}
        userUuid={currentUser?.uuid}
        interviewId={interviewId}
      />

      <DocumentHeader title="Share Your Interview Experience" />
      <div data-body-bg="white" className="bg-white">
        <MinimalHeader isSticky />
        <div className="sticky top-16 z-10 h-1 bg-gray-100">
          <div
            className="h-full bg-indigo-600 transition-all duration-300 ease-in-out rounded-r-full"
            style={{width: `${((incomingStep + 1) / STEPS.length) * 100}%`}}
          />
        </div>
        <div className="flex">
          {/* Left panel: form (scrolls with the page) */}
          <div className="w-full md:w-1/2">
            <div className="px-6 pt-8 pb-32 max-w-lg mx-auto w-full">
              <DismissibleCallout id="contribute-example-interview" className="mb-6 text-sm rounded-lg">
                {'Recently accepted interview experience: '}
                <Link
                  href="/experiences/anthropic-senior-software-engineer-interview-2ffa5f"
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                  target="_blank"
                >
                  L5 SWE at Anthropic
                </Link>
              </DismissibleCallout>
              <StepIndicator step={incomingStep} />
              <div
                className={cx(
                  'transform transition-all ease-in-out',
                  animationPhase === 'leaving' && 'duration-200 opacity-0',
                  animationPhase === 'leaving' && incomingStep > step && '-translate-x-6',
                  animationPhase === 'leaving' && incomingStep < step && 'translate-x-6',
                  animationPhase === 'entering' && 'duration-0 opacity-0',
                  animationPhase === 'entering' && incomingStep > step && 'translate-x-6',
                  animationPhase === 'entering' && incomingStep < step && '-translate-x-6',
                  animationPhase === 'idle' && 'duration-300 opacity-100 translate-x-0',
                )}
              >
                {displayStep === 0 && <OverviewStep {...stepProps} tags={tags} />}
                {displayStep === 1 && <ProcessStep {...stepProps} tags={tags} />}
                {displayStep === 2 && <QuestionsStep {...stepProps} />}
                {displayStep === 3 && (
                  <ReflectionsStep {...stepProps} isSubmitting={isSubmitting} />
                )}
                {displayStep === 4 && <SubmittingStep />}
              </div>
              {error && (
                <div className="mt-4 text-sm text-red-600 font-medium">{error}</div>
              )}
              {loadedFromRemoteDraft && displayStep < STEPS.length - 1 && (
                <div className="mt-4 flex justify-center">
                  <Button
                    type="button"
                    buttonType={ButtonTypes.TERTIARY}
                    onClick={onReset}
                    className="text-gray-600"
                  >
                    Start over with a new interview
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: live preview, sticky while the page scrolls (hidden on mobile) */}
          <aside className="hidden md:flex w-1/2 px-3 pt-3 pb-5 sticky top-[68px] self-start h-[calc(100vh-68px)]">
            <div className="flex-1 flex flex-col min-h-0">
              <InterviewPreview
                draft={draft}
                tags={tags}
                displayStep={displayStep}
              />
            </div>
          </aside>
        </div>
      </div>
    </>
  );
};

export default AddInterviewPage;
